/**
 * Architecture Validator
 * Monitors architectural compliance and data quality in real-time
 */

export interface ArchitectureViolation {
  id: string;
  type: 'hardcoded_year' | 'data_quality' | 'performance' | 'type_safety' | 'error_boundary';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  file?: string;
  line?: number;
  details?: string;
  timestamp: Date;
}

export interface ValidationResult {
  isValid: boolean;
  violations: ArchitectureViolation[];
  summary: {
    total: number;
    bySeverity: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
  };
}

export class ArchitectureValidator {
  private static instance: ArchitectureValidator;
  private violations: ArchitectureViolation[] = [];
  private isMonitoring: boolean = false;

  private constructor() {}

  public static getInstance(): ArchitectureValidator {
    if (!ArchitectureValidator.instance) {
      ArchitectureValidator.instance = new ArchitectureValidator();
    }
    return ArchitectureValidator.instance;
  }

  /**
   * Run a full architectural validation
   */
  public async runFullValidation(): Promise<ValidationResult> {
    this.violations = [];
    
    // Check for hardcoded years
    this.checkHardcodedYears();
    
    // Check data quality
    await this.checkDataQuality();
    
    // Check performance metrics
    this.checkPerformanceMetrics();
    
    // Check type safety
    this.checkTypeSafety();
    
    // Check error boundaries
    this.checkErrorBoundaries();

    return this.generateValidationResult();
  }

  /**
   * Start continuous monitoring
   */
  public startMonitoring(intervalMs: number = 30000): void {
    if (this.isMonitoring) {
      console.warn('Architecture monitoring is already running');
      return;
    }

    this.isMonitoring = true;
    console.log('🔍 Starting architecture monitoring...');

    setInterval(async () => {
      try {
        const result = await this.runFullValidation();
        this.processValidationResult(result);
      } catch (error) {
        console.error('Architecture validation failed:', error);
      }
    }, intervalMs);
  }

  /**
   * Stop continuous monitoring
   */
  public stopMonitoring(): void {
    this.isMonitoring = false;
    console.log('🛑 Architecture monitoring stopped');
  }

  /**
   * Check for hardcoded years in the current page
   */
  private checkHardcodedYears(): void {
    if (typeof window === 'undefined') return;

    const currentYear = new Date().getFullYear();
    const hardcodedYears = ['2024', '2025', '2023'];
    
    // Check page content for hardcoded years
    const pageText = document.body.innerText;
    
    hardcodedYears.forEach(year => {
      if (pageText.includes(year)) {
        this.recordViolation({
          id: `hardcoded-year-${year}-${Date.now()}`,
          type: 'hardcoded_year',
          severity: 'medium',
          message: `Hardcoded year ${year} found in page content`,
          details: `Year ${year} should be dynamically derived from current date`,
          timestamp: new Date()
        });
      }
    });
  }

  /**
   * Check data quality metrics
   */
  private async checkDataQuality(): Promise<void> {
    try {
      // Check if data is loading properly - look for actual chart elements
      const chartElements = document.querySelectorAll('.echarts, [class*="chart"], canvas, svg');
      const dataElements = document.querySelectorAll('[data-testid*="chart"], [data-testid*="data"]');
      const loadingElements = document.querySelectorAll('[class*="loading"], [class*="spinner"]');
      
      // Only report violation if no chart elements AND no loading elements (page fully loaded)
      if (chartElements.length === 0 && dataElements.length === 0 && loadingElements.length === 0) {
        // Check if we're still in the initial loading phase
        const hasReactRoot = document.querySelector('#root, #app, [data-reactroot]');
        if (hasReactRoot) {
          this.recordViolation({
            id: `no-data-elements-${Date.now()}`,
            type: 'data_quality',
            severity: 'high',
            message: 'No data elements found on page',
            details: 'Expected chart or data elements not present after page load',
            timestamp: new Date()
          });
        }
      }

      // Check for error states
      const errorElements = document.querySelectorAll('[data-testid*="error"], .error, [class*="error"]');
      if (errorElements.length > 0) {
        this.recordViolation({
          id: `error-elements-${Date.now()}`,
          type: 'data_quality',
          severity: 'high',
          message: `${errorElements.length} error elements found`,
          details: 'Data loading or rendering errors detected',
          timestamp: new Date()
        });
      }
    } catch (error) {
      this.recordViolation({
        id: `data-quality-check-failed-${Date.now()}`,
        type: 'data_quality',
        severity: 'medium',
        message: 'Data quality check failed',
        details: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date()
      });
    }
  }

  /**
   * Check performance metrics
   */
  private checkPerformanceMetrics(): void {
    if (typeof window === 'undefined' || !window.performance) return;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      const loadTime = navigation.loadEventEnd - navigation.loadEventStart;
      
      if (loadTime > 3000) {
        this.recordViolation({
          id: `slow-load-${Date.now()}`,
          type: 'performance',
          severity: 'medium',
          message: `Page load time is ${loadTime.toFixed(0)}ms`,
          details: 'Page load time exceeds 3 seconds threshold',
          timestamp: new Date()
        });
      }

      const domContentLoaded = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
      if (domContentLoaded > 1000) {
        this.recordViolation({
          id: `slow-dom-${Date.now()}`,
          type: 'performance',
          severity: 'low',
          message: `DOM content loaded in ${domContentLoaded.toFixed(0)}ms`,
          details: 'DOM content loading exceeds 1 second threshold',
          timestamp: new Date()
        });
      }
    }
  }

  /**
   * Check type safety (basic checks)
   */
  private checkTypeSafety(): void {
    if (typeof window === 'undefined') return;

    // Check for console errors that might indicate type issues
    const originalError = console.error;
    const errors: string[] = [];
    
    console.error = (...args: any[]) => {
      errors.push(args.join(' '));
      originalError.apply(console, args);
    };

    // Restore after a short delay
    setTimeout(() => {
      console.error = originalError;
      
      if (errors.length > 0) {
        this.recordViolation({
          id: `console-errors-${Date.now()}`,
          type: 'type_safety',
          severity: 'medium',
          message: `${errors.length} console errors detected`,
          details: errors.slice(0, 3).join('; '),
          timestamp: new Date()
        });
      }
    }, 1000);
  }

  /**
   * Check for error boundaries
   */
  private checkErrorBoundaries(): void {
    if (typeof window === 'undefined') return;

    // Check if error boundary components are present
    const errorBoundaries = document.querySelectorAll('[data-testid*="error-boundary"], .error-boundary');
    
    if (errorBoundaries.length === 0) {
      this.recordViolation({
        id: `no-error-boundaries-${Date.now()}`,
        type: 'error_boundary',
        severity: 'low',
        message: 'No error boundary components found',
        details: 'Consider adding error boundaries for better error handling',
        timestamp: new Date()
      });
    }
  }

  /**
   * Record a violation
   */
  private recordViolation(violation: ArchitectureViolation): void {
    this.violations.push(violation);
    console.warn(`🚨 Architecture Violation [${violation.severity.toUpperCase()}]:`, violation.message);
  }

  /**
   * Process validation results
   */
  private processValidationResult(result: ValidationResult): void {
    if (!result.isValid) {
      console.warn(`⚠️  Architecture validation failed: ${result.summary.total} violations found`);
      
      // Log critical and high severity violations
      const criticalViolations = result.violations.filter(v => v.severity === 'critical' || v.severity === 'high');
      if (criticalViolations.length > 0) {
        console.error('🚨 Critical violations:', criticalViolations.map(v => v.message));
      }
    } else {
      console.log('✅ Architecture validation passed');
    }
  }

  /**
   * Generate validation result
   */
  private generateValidationResult(): ValidationResult {
    const bySeverity = {
      critical: this.violations.filter(v => v.severity === 'critical').length,
      high: this.violations.filter(v => v.severity === 'high').length,
      medium: this.violations.filter(v => v.severity === 'medium').length,
      low: this.violations.filter(v => v.severity === 'low').length,
    };

    return {
      isValid: this.violations.length === 0,
      violations: [...this.violations],
      summary: {
        total: this.violations.length,
        bySeverity
      }
    };
  }

  /**
   * Get current violations
   */
  public getViolations(): ArchitectureViolation[] {
    return [...this.violations];
  }

  /**
   * Clear violations
   */
  public clearViolations(): void {
    this.violations = [];
  }
}
